/**
 * lib/jumpTo.js — Iter 48 cross-component Jump Navigation.
 *
 * Dispatches a `forgedops:jump` CustomEvent. Any consumer (today: the
 * SheetViewer; tomorrow: ProjectShell for cross-sheet hops) listens and
 * performs the navigation while preserving camera state where possible.
 *
 * The relationship-engine API ships every related-object envelope and
 * every search result with `sheet_id`, `normalized_x`, `normalized_y`,
 * `object_type`, `object_id`. Callers fire {@link dispatchJump} with
 * those fields — the navigation system handles routing + animation.
 */

export const JUMP_EVENT = "forgedops:jump";

/**
 * Fire a Jump intent.
 *
 * @param {object}  target
 * @param {string}  target.object_type    — structure | pin | revision | measurement | utility_watch | photo | issue
 * @param {string}  target.object_id
 * @param {string=} target.sheet_id        — if known
 * @param {number=} target.normalized_x    — [0..1] for animated centering
 * @param {number=} target.normalized_y
 * @param {string=} target.label           — for transient announcement
 */
export function dispatchJump(target) {
  if (!target || !target.object_type || !target.object_id) return;
  try {
    window.dispatchEvent(new CustomEvent(JUMP_EVENT, { detail: target }));
  } catch (_) {
    /* no DOM (SSR/tests) — silent */
  }
}

/**
 * Subscribe to Jump intents. Returns the unsubscribe function.
 *
 * @param {(target: object) => void} handler
 * @returns {() => void}
 */
export function onJump(handler) {
  if (typeof window === "undefined") return () => {};
  const fn = (e) => handler && handler(e.detail);
  window.addEventListener(JUMP_EVENT, fn);
  return () => window.removeEventListener(JUMP_EVENT, fn);
}
